import Controller from "./application_controller"

/**
 * Dispatches `appearance:visible` when this element becomes rendered and
 * visible. The controller observes this element and its ancestors for
 * visibility-related attribute changes, so it catches hidden containers such as
 * legacy modals when they are shown.
 *
 * Use it with another controller that should delay work until visible:
 *
 *   data-controller="cloudflare--turnstile appearance"
 *   data-action="appearance:visible->cloudflare--turnstile#render"
 */
export default class extends Controller {
  visible = false
  animationFrame = null
  mutationObserver = null

  connect() {
    this.visible = false
    this.observeVisibilityAttributes()
    this.scheduleRefresh()
  }

  disconnect() {
    super.disconnect()
    this.mutationObserver?.disconnect()

    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame)
    }
  }

  refresh() {
    this.animationFrame = null

    const visible = this.isVisible()

    if (visible && !this.visible) {
      this.dispatch("visible")
    }

    this.visible = visible
  }

  isVisible() {
    if (typeof this.element.checkVisibility === "function") {
      return this.element.checkVisibility({ visibilityProperty: true })
    }

    const styles = window.getComputedStyle(this.element)

    if (styles.display == "none" || styles.visibility == "hidden") {
      return false
    }

    return this.element.getClientRects().length > 0
  }

  observeVisibilityAttributes() {
    this.mutationObserver = new MutationObserver(() => this.scheduleRefresh())

    this.visibilityAncestors.forEach((element) => {
      this.mutationObserver.observe(element, {
        attributes: true,
        attributeFilter: ["class", "hidden", "open", "style"]
      })
    })
  }

  scheduleRefresh() {
    this.animationFrame ??= requestAnimationFrame(() => this.refresh())
  }

  get visibilityAncestors() {
    const ancestors = []
    let element = this.element

    while (element && element.nodeType == Node.ELEMENT_NODE) {
      ancestors.push(element)
      element = element.parentElement
    }

    return ancestors
  }
}
